import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Squircle, Loader, Lock, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithGoogle, signInWithMicrosoft, handleOAuthCallback } from '../services/authService';

//parties
import Particles, { initParticlesEngine } from "@tsparticles/react";
import {
  type Container,
} from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";
import { particleOptions } from "./particleConfig";

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithOAuth, isLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);

  const [init, setInit] = useState(false);

  // Get redirect destination from URL or default to dashboard
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError(t('loginError'));
      return;
    }

    try {
      const success = await login(username, password);
      if (success) {
        // Redirect to the page they came from or dashboard
        navigate(from, { replace: true });
      } else {
        setError(t('invalidLoginCredentials'));
      }
    } catch (err) {
      console.error(err);
      setError(t('loginFailed'));
    }
  };

  // Helper function to auto-fill demo credentials
  const fillDemoCredentials = (role: 'user' | 'superuser') => {
    if (role === 'user') {
      setUsername('regular_user');
      setPassword('password');
    } else {
      setUsername('super_user');
      setPassword('password');
    }
  };

  // Handle Google OAuth sign-in
  const handleGoogleSignIn = async () => {
    setError(null);
    setOauthLoading(true);
    
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        const success = await loginWithOAuth(result);
        if (success) {
          navigate(from, { replace: true });
        } else {
          setError(t('loginFailed'));
        }
      } else {
        setError(result.error || t('loginFailed'));
      }
    } catch (err) {
      console.error(err);
      setError(t('loginFailed'));
    } finally {
      setOauthLoading(false);
    }
  };

  // Handle Microsoft/Active Directory OAuth sign-in
  const handleMicrosoftSignIn = async () => {
    setError(null);
    setOauthLoading(true);
    
    try {
      const result = await signInWithMicrosoft();
      if (result.success) {
        const success = await loginWithOAuth(result);
        if (success) {
          navigate(from, { replace: true });
        } else {
          setError(t('loginFailed'));
        }
      } else {
        setError(result.error || t('loginFailed'));
      }
    } catch (err) {
      console.error(err);
      setError(t('loginFailed'));
    } finally {
      setOauthLoading(false);
    }
  };


  // this should be run only once per application lifetime
  useEffect(() => {
    const initializeParticles = async () => {
      try {
        await initParticlesEngine(async (engine) => {
          await loadSlim(engine);
        });
        setInit(true);
      } catch (error) {
        console.error("Error initializing particles engine:", error);
      }
    };
    
    // Check for OAuth callback
    const oauthResult = handleOAuthCallback();
    if (oauthResult) {
      loginWithOAuth(oauthResult).then(success => {
        if (success) {
          navigate(from, { replace: true });
        }
      });
    }
    
    fillDemoCredentials('user'); // Fill demo credentials for easy testing
    initializeParticles();
  }, []);

  const particlesLoaded = async (container?: Container): Promise<void> => {
    console.log("Particles loaded:", container);
  };

  const options = useMemo(() => particleOptions, []);
  

  if (init) {
    return (

      <div className="relative">
        <Particles id="tsparticles" particlesLoaded={particlesLoaded} options={options}         className="absolute inset-0 -z-3"  />
        <div className="relative z-10 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md bg-white/40 dark:bg-gray-800/40 p-8 rounded-lg shadow-lg">


          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              {t('loginTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              {t('loginSubtitle')}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4 rounded-md shadow-sm">
              <div>
                <label htmlFor="username" className="sr-only">
                  {t('usernameField')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:placeholder:text-gray-500"
                    placeholder={t('username')} />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="sr-only">
                  {t('password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:placeholder:text-gray-500"
                    placeholder={t('password')} />
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Squircle className="h-5 w-5 text-red-400 dark:text-red-500" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || oauthLoading}
                className="group relative flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-70"
              >
                {isLoading ? (
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                ) : null}
                {t('signIn')}
              </button>
              <div>&nbsp;</div>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading || oauthLoading}
                className="group relative flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-70"
              >
                {oauthLoading ? (
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                ) : null}
                {t('googleSignIn')}
              </button>
              <div>&nbsp;</div>
              <button
                type="button"
                onClick={handleMicrosoftSignIn}
                disabled={isLoading || oauthLoading}
                className="group relative flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-70"
              >
                {oauthLoading ? (
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                ) : null}
                {t('msftadssoBtnText')}
              </button>
            </div>
          </form>

          {/* Demo accounts for easy testing */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-gray-50 dark:bg-gray-900 px-2 text-gray-500 dark:text-gray-400">
                  {t('loginwithdemoaccounts')}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={() => fillDemoCredentials('user')}
                className="flex w-full justify-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('regularUser')}
              </button>
              <button
                onClick={() => fillDemoCredentials('superuser')}
                className="flex w-full justify-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('superUser')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      </div> // end of relative min-h-screen div

    );

  }

  return <div>Loading particles...</div>;

};

export default LoginPage;
